import { useIntl } from 'react-intl'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const DesktopPanel = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: contents;
  }
`

const UnavailablePackages = styled(Group)`
  flex: 1;
  margin: ${rem(16)};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.L};
`

const UNAVAILABLE_ICON = <LineExclamationTriangleIcon size="S" />

const UNAVAILABLE_PACKAGE_SELECTION_STATES: ReadonlySet<AccommodationRoomTileActionsState> = new Set([
  'sold-out-on-dates',
  'sold-out-on-dates-or-exceeded-capacity',
  'exceeded-capacity',
])

export function isUnavailablePackageSelectionState(state: AccommodationRoomTileActionsState | undefined): boolean {
  return !!state && UNAVAILABLE_PACKAGE_SELECTION_STATES.has(state)
}

interface Props {
  onChangeDates: () => void
  onBookDifferentRoom?: () => void
}

function PackageSelectionUnavailablePackages({ onChangeDates, onBookDifferentRoom }: Props) {
  const intl = useIntl()
  const unavailableMessage = intl.formatMessage({
    id: 'M134QhaZ5aEMN4X12OIz',
    defaultMessage: 'This room is unavailable for your dates or guest count.',
    description: 'Warning shown in the package column when a room has no package for the selected dates or guests',
  })
  const changeDatesLabel = intl.formatMessage({
    id: '2l1tjDNrBU6BW2WdyFZy',
    defaultMessage: 'Change dates',
    description: 'Action in the unavailable room package column that opens the date editor',
  })
  const bookDifferentRoomLabel = intl.formatMessage({
    id: 'HQSiGfDZBzrwquyKgQPF',
    defaultMessage: 'Book a different room',
    description: 'Action in the unavailable room package column that navigates to the cheapest available package',
  })

  return <DesktopPanel>
    <UnavailablePackages direction="vertical" horizontalAlign="center" verticalAlign="center" gap={12}>
      <BodyTextBlock variant="medium" align="center" startIcon={UNAVAILABLE_ICON}>{unavailableMessage}</BodyTextBlock>
      <Group direction="horizontal" verticalAlign="center" gap={8}>
        <TextLink size="small" onClick={onChangeDates}>{changeDatesLabel}</TextLink>
        {onBookDifferentRoom && <TextButton kind="secondary" size="small" onClick={onBookDifferentRoom}>{bookDifferentRoomLabel}</TextButton>}
      </Group>
    </UnavailablePackages>
  </DesktopPanel>
}

export default PackageSelectionUnavailablePackages
